import { Injectable, inject } from '@angular/core';

import {
    DeviceService,
    HtmlNode,
    NativeAppService,
    NavigationService,
    OnFeatureInit,
    Page,
    ProductMetadata,
    ProductService,
    UserService,
    UserUpdateEvent,
    ViewTransitionUtils,
} from '@frontend/vanilla/core';
import { RouteDataService } from '@frontend/vanilla/shared/routing';
import { filter } from 'rxjs/operators';

@Injectable()
export class HtmlBootstrapService implements OnFeatureInit {
    private user = inject(UserService);
    private htmlNode = inject(HtmlNode);
    private page = inject(Page);
    private productService = inject(ProductService);
    private deviceService = inject(DeviceService);
    private nativeAppService = inject(NativeAppService);
    private routeDataService = inject(RouteDataService);
    private navigationService = inject(NavigationService);

    readonly #viewTransitionUtils = inject(ViewTransitionUtils);

    onFeatureInit() {
        this.htmlNode.setAttribute('data-domain', this.page.domain);
        this.htmlNode.setCssClass(`th-${this.page.theme}`, true);
        this.htmlNode.setCssClass('device-mobile', this.deviceService.isMobile);
        this.htmlNode.setCssClass('device-mobile-phone', this.deviceService.isMobilePhone);
        this.htmlNode.setCssClass('device-tablet', this.deviceService.isTablet);
        this.htmlNode.setCssClass('device-desktop', !this.deviceService.isMobile);
        this.htmlNode.setCssClass('device-touch', this.deviceService.isTouch);

        this.htmlNode.setCssClass('native-download-client-app', this.nativeAppService.isDownloadClientApp);
        this.htmlNode.setCssClass('native-download-client-wrapper', this.nativeAppService.isDownloadClientWrapper);
        this.htmlNode.setCssClass('native-app', this.nativeAppService.isNativeApp);
        this.htmlNode.setCssClass('native-wrapper', this.nativeAppService.isNativeWrapper && !this.nativeAppService.isNativeWrapperODR);
        this.htmlNode.setCssClass('native-wrapper-odr', this.nativeAppService.isNativeWrapperODR);

        if (!this.productService.isSingleDomainApp) {
            this.htmlNode.setCssClass(`product-${this.page.product}`, true);
        } else {
            this.productService.productChanged.pipe(this.#viewTransitionUtils.deferUntilTransitionReady()).subscribe((product: ProductMetadata) => {
                this.htmlNode.removeClassStartsWith(`product-`);
                this.htmlNode.setCssClass(`product-${product.name}`, true);
            });
            this.navigationService.locationChange.pipe(this.#viewTransitionUtils.deferUntilTransitionReady()).subscribe(() => {
                const vanillaRoute = this.routeDataService.get('vanillaRoute');
                if (vanillaRoute) {
                    this.htmlNode.setCssClass('vanilla-route', true);
                } else {
                    this.htmlNode.setCssClass('vanilla-route', false);
                }
            });
        }
        if (this.nativeAppService.isNative) {
            this.htmlNode.setCssClass(`native-${this.nativeAppService.applicationName}`, true);
        }

        this.setCountryAttribute();
        this.setHtmlClass();

        this.user.events.pipe(filter((e): e is UserUpdateEvent => e instanceof UserUpdateEvent)).subscribe((e: UserUpdateEvent) => {
            const diff = e.diff;

            if (diff.has('workflowType') || diff.has('isAuthenticated')) {
                this.setHtmlClass();
            }

            if (diff.has('country') || diff.has('isAuthenticated')) {
                this.setCountryAttribute();
            }
        });
    }

    private setCountryAttribute() {
        if (this.user.isAuthenticated) {
            this.htmlNode.setAttribute('data-country', this.user.country);
        } else {
            this.htmlNode.setAttribute('data-country', null);
        }
    }

    private setHtmlClass() {
        const hasWorkflow = this.user.workflowType !== 0;

        this.htmlNode.setCssClass('has-workflow', hasWorkflow);
        this.htmlNode.setCssClass('unauthenticated', !this.user.isAuthenticated);
        this.htmlNode.setCssClass('authenticated', this.user.isAuthenticated);
    }
}
